import { sharedLib } from "@shared/services/shared.service";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DisplayResultMessage,
  LanguageNameSpaces,
  OpenMessage,
  PlanUsageLimits,
  SubscriptionPlanType,
  UsageLimitValue,
  UsageType,
} from "@shared/types";
import pluralize from "pluralize";
import { isNumber } from "lodash";
import {
  checkPassedQuota,
  getUsageInlineKey,
  getUsageTitleKey,
} from "@shared/features/settings/utils";
import {
  useClientsData,
  useUser,
  useUserUsageTypes,
} from "@shared/utils/hooks/misc";
import { useTheme } from "styled-components";
import moment from "moment";
import { getSubscriptionTypeDisplay } from "@shared/utils/misc";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import { skipToken } from "@reduxjs/toolkit/query";
import { QuotaHandleTriggerClick } from "@shared/features/settings/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useSharedQuotaModalProps {
  usageType?: UsageType;
  onTriggerClick?: () => void;
  checkImmediately?: boolean;
  onModalClose?: () => void;
  displayResultMessage?: DisplayResultMessage;
  isWebView?: boolean;
}
export const useSharedQuotaModal = ({
  usageType,
  onTriggerClick,
  checkImmediately,
  onModalClose,
  displayResultMessage,
  isWebView,
}: useSharedQuotaModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const {
    isLoading: isLoadingUsageLimits,
    userUsageLimits,
    getNextPlanData: getNextPlan,
    getTotalQuota,
    getExtraQuota,
  } = useUserUsageLimits();
  const [getAllQuotaUsage, getAllQuotaUsageStatus] =
    sharedLib.sharedApi.settings.endpoints.getAllQuotaUsage.useLazyQuery();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingQuotaUsage, setIsLoadingQuotaUsage] = useState(false);
  const { user } = useUser(true);
  const usageTypes = useUserUsageTypes();

  const baseUsageLimit = usageType ? userUsageLimits?.[usageType] : undefined;
  const totalUsageLimit = usageType
    ? getTotalQuota(usageType, userUsageLimits?.[usageType])
    : undefined;
  const allQuotaUsage = getAllQuotaUsageStatus.data;
  const currentUsage = (usageType && allQuotaUsage?.[usageType]) ?? 0;
  const isLoading = isLoadingUsageLimits || isLoadingQuotaUsage;
  const isSubscriptionExpired = user?.isExpired();
  const nextPlanData = useMemo(
    () => getNextPlan(currentUsage, usageType),
    [currentUsage, usageType]
  );
  const triggerClickDataRef = useRef<{
    onTriggerClick?: () => void;
  }>(undefined);
  triggerClickDataRef.current = {
    onTriggerClick,
  };

  const handleTriggerClick: QuotaHandleTriggerClick = useCallback(
    async (args) => {
      const {
        numItems = 1,
        closeModal,
        allowDuringGrace,
        ignoreQuotaCheck,
      } = args || {};
      const handleOpenModal = () => {
        if (closeModal) {
          new Promise<void>((resolve) => {
            closeModal();
            setTimeout(() => resolve(), 400);
          }).then(() => setIsOpen(true));
        } else {
          setIsOpen(true);
        }
      };

      if (isSubscriptionExpired && !allowDuringGrace) {
        handleOpenModal();
        return Promise.resolve(true);
      }
      if (!usageType || ignoreQuotaCheck) {
        triggerClickDataRef.current?.onTriggerClick?.();
        return Promise.resolve(false);
      }
      setIsLoadingQuotaUsage(true);
      return displayResultMessage?.(getAllQuotaUsage().unwrap())
        .then((response?: PlanUsageLimits) => {
          const currentUsage = response?.[usageType];
          const didPassQuota =
            currentUsage !== undefined &&
            !!totalUsageLimit &&
            typeof currentUsage === "number" &&
            typeof totalUsageLimit === "number" &&
            currentUsage + numItems > totalUsageLimit;
          if (didPassQuota) {
            handleOpenModal();
          } else {
            triggerClickDataRef.current?.onTriggerClick?.();
          }
          return Promise.resolve(didPassQuota);
        })
        .catch(() => {})
        .finally(() => setIsLoadingQuotaUsage(false));
    },
    [user, totalUsageLimit, usageType]
  );

  useEffect(() => {
    if (checkImmediately) {
      handleTriggerClick().catch(() => {});
    }
  }, [checkImmediately]);

  const getSortedUsageTypes = () => {
    const getUsageDifference = (usageType: UsageType) => {
      const currentUsage = allQuotaUsage?.[usageType];
      let usageLimit = getTotalQuota(usageType, userUsageLimits?.[usageType]);
      if (currentUsage === "inf") return -1;
      else if (usageLimit === "inf") usageLimit = 999999999;
      if (typeof currentUsage === "number" && typeof usageLimit === "number") {
        return currentUsage / usageLimit;
      } else if (typeof currentUsage === "number") return 1;
      else if (typeof usageLimit === "number") return -1;
      return 0;
    };

    return usageTypes.slice().sort((a, b) => {
      return getUsageDifference(b) - getUsageDifference(a);
    });
  };

  const getNextPlanData = () => {
    if (!nextPlanData) return;
    const sortedUsageTypes = getSortedUsageTypes();
    const filteredUsageLimits = sortedUsageTypes.slice(0, 3);
    if (
      nextPlanData.type === SubscriptionPlanType.ASSET_MANAGER &&
      filteredUsageLimits.length === 3
    ) {
      filteredUsageLimits[2] = UsageType.SECURE_CLIENT_INVITE;
    }
    return {
      name: getSubscriptionTypeDisplay(nextPlanData.type),
      limits: filteredUsageLimits.map((usageType) => {
        const limit = nextPlanData.usageLimits?.[usageType];
        const baseLimit = limit === "inf" ? "Unlimited" : limit;
        return {
          usageType,
          baseLimit,
          extraLimit: getExtraQuota(usageType),
          objectName: t(getUsageTitleKey(usageType, nextPlanData.type)),
        };
      }),
    };
  };

  const pluralizedObjectName = usageType
    ? pluralize(
        t(getUsageInlineKey(usageType)),
        isNumber(totalUsageLimit) ? totalUsageLimit : 2
      )
    : undefined;

  const currentUsageObjectText = `${totalUsageLimit} ${pluralizedObjectName}`;
  const isMaxPlan = !!user?.isMaxSubscription();
  const isClientQuota = usageType === UsageType.CLIENT;
  const isMaxPlanClientQuota = isMaxPlan && isClientQuota;
  const isPremiumPlanClientQuota = !!user?.isPro() && isClientQuota;
  const currentUsageName = isMaxPlanClientQuota
    ? "Primary API Slots"
    : "Current Usage";

  const getTitle = () => {
    if (isSubscriptionExpired) return "Subscription expired";
    if (isMaxPlanClientQuota)
      return "You have reached your Primary API Slots limit";
    return "You have reached your plan's limit";
  };

  const getOkText = () => {
    if (isSubscriptionExpired) return "Renew Subscription";
    if (isWebView && (isMaxPlanClientQuota || isPremiumPlanClientQuota))
      return "Contact Support";
    if (isMaxPlanClientQuota) return "Purchase More";
    if (isPremiumPlanClientQuota) return "Purchase Additional Slots";
  };

  const onCancel = useCallback(() => {
    setIsOpen(false);
    onModalClose?.();
  }, []);

  return {
    handleTriggerClick,
    isOpen,
    onCancel,
    title: getTitle(),
    currentUsageObjectText,
    isLoading,
    currentUsage,
    usageLimit: baseUsageLimit,
    nextPlanData: getNextPlanData(),
    usageType,
    currentUsageName,
    isSubscriptionExpired,
    okText: getOkText(),
    isMaxPlan,
    isMaxPlanClientQuota,
    isPremiumPlanClientQuota,
  };
};

export const useQuotaTest = () => {
  const { getTotalQuota, getExtraQuota } = useUsageLimits();

  const testQuota = (
    usageType: UsageType,
    usage?: UsageLimitValue,
    limit?: UsageLimitValue,
    graceRatio?: number
  ) => {
    const totalQuota = getTotalQuota(usageType, limit);
    return checkPassedQuota(usage, totalQuota, graceRatio);
  };

  return {
    testQuota,
    getExtraQuota,
    getTotalQuota,
  };
};

export const useUsageLimits = (refetchOnMountOrArgChange?: boolean) => {
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { data: usageLimitsData, ...getUsageLimitsStatus } =
    sharedLib.sharedApi.settings.useGetUsageLimitsQuery(
      isExternalConnectAccount ? skipToken : undefined,
      {
        refetchOnMountOrArgChange,
      }
    );
  const usageLimits = usageLimitsData?.limits;
  const additionalLimits = usageLimitsData?.additional_limits;

  const getUsageLimits = (planType: SubscriptionPlanType) =>
    usageLimits?.[planType];

  const usageTypeToExtraLimits = useMemo(() => {
    return additionalLimits
      ?.filter((limit) => limit.expiration_timestamp > moment().unix())
      .reduce((acc: { [key in UsageType]?: number }, usageLimit) => {
        const usageType = usageLimit.usage_type;
        const quota = usageLimit.quota;
        const prevValue = acc[usageType];
        if (prevValue) {
          acc[usageType] = prevValue + quota;
        } else {
          acc[usageType] = quota;
        }
        return acc;
      }, {});
  }, [additionalLimits]);

  const getExtraQuota = (usageType: UsageType) => {
    return usageTypeToExtraLimits?.[usageType];
  };

  const getTotalQuota = (usageType: UsageType, limit?: UsageLimitValue) => {
    const extraQuota = getExtraQuota(usageType);
    return typeof limit === "number" && extraQuota ? limit + extraQuota : limit;
  };

  return {
    usageLimits,
    getUsageLimits,
    getExtraQuota,
    getTotalQuota,
    getUsageLimitsStatus,
  };
};

export const useUserUsageLimits = (refetchOnMountOrArgChange?: boolean) => {
  const {
    usageLimits,
    getUsageLimits,
    getUsageLimitsStatus,
    getTotalQuota,
    getExtraQuota,
  } = useUsageLimits(refetchOnMountOrArgChange);
  const { user } = useUser();

  const userSubscriptionPlan = user?.subscription_plan_type;
  const userUsageLimits = userSubscriptionPlan
    ? getUsageLimits(userSubscriptionPlan)
    : undefined;
  const allPlans = Object.values(
    SubscriptionPlanType
  ) as SubscriptionPlanType[];

  const getNextPlanType = (
    planIndex: number,
    currentUsage: UsageLimitValue,
    usageType: UsageType
  ) => {
    return (
      allPlans.slice(planIndex + 1).find((plan) => {
        const planUsageLimit = getUsageLimits(plan)?.[usageType];
        if (planUsageLimit && planUsageLimit > currentUsage) {
          return plan;
        }
      }) || allPlans[planIndex + 1]
    );
  };

  const getNextPlanData = (
    currentUsage: UsageLimitValue,
    usageType?: UsageType
  ) => {
    if (!userSubscriptionPlan || user?.isMaxSubscription()) return;
    const currentPlanIndex = allPlans.indexOf(userSubscriptionPlan);
    const nextPlanType = usageType
      ? getNextPlanType(currentPlanIndex, currentUsage, usageType)
      : allPlans[currentPlanIndex + 1];

    return {
      type: nextPlanType,
      usageLimits: getUsageLimits(nextPlanType),
    };
  };

  return {
    userUsageLimits,
    usageLimits,
    isLoading: getUsageLimitsStatus.isLoading,
    getNextPlanData,
    getTotalQuota,
    getExtraQuota,
  };
};

export const useCheckQuotaUsage = (
  checkImmediately?: boolean,
  planType?: SubscriptionPlanType
) => {
  const { data: usageLimitsData } =
    sharedLib.sharedApi.settings.useGetUsageLimitsQuery();
  const [getAllQuotaUsage, getAllQuotaUsageStatus] =
    sharedLib.sharedApi.settings.endpoints.getAllQuotaUsage.useLazyQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [isQuotaError, setIsQuotaError] = useState(false);
  const usageLimits = usageLimitsData?.limits;
  const { testQuota, getExtraQuota } = useQuotaTest();
  const usageTypes = useUserUsageTypes();

  const checkQuotaUsage = (planType?: SubscriptionPlanType) => {
    if (!planType || !usageLimits) return;

    const planUsageLimits = usageLimits[planType];
    setIsLoading(true);
    return getAllQuotaUsage()
      .unwrap()
      .then((quotaUsage) => {
        const hasQuotaError = usageTypes.some((usageType) => {
          return (
            usageType !== UsageType.BACKTESTING &&
            testQuota(
              usageType,
              quotaUsage?.[usageType],
              planUsageLimits?.[usageType],
              usageType === UsageType.ACTIVE_TRADE ? 0.3 : undefined
            )
          );
        });
        setIsQuotaError(hasQuotaError);
        return Promise.resolve(hasQuotaError);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (checkImmediately && usageLimits && planType) checkQuotaUsage(planType);
  }, [checkImmediately, !!usageLimits, planType]);

  return {
    checkQuotaUsage,
    isLoading,
    allQuotaUsage: getAllQuotaUsageStatus.data,
    isQuotaError,
    getExtraQuota,
  };
};

export const useInvalidAccount = () => {
  const theme = useTheme();
  const { getIsAccountInvalid, getIsAccountInactive } = useClientsData();

  const getInvalidAccountData = useCallback(
    (accountId?: number) => {
      const isInvalid = getIsAccountInvalid(accountId);
      const isInactive = getIsAccountInactive(accountId);
      let color: string | undefined;
      if (isInvalid) color = theme.colors.red;
      if (isInactive) color = theme.colors.darkYellow;
      return {
        isInvalidOrInactive: isInvalid || isInactive,
        isInvalid,
        isInactive,
        color,
      };
    },
    [getIsAccountInactive, getIsAccountInvalid]
  );

  return {
    getInvalidAccountData,
  };
};

interface useDeleteAccountProps {
  openMessage: OpenMessage;
}
export const useDeleteAccount = ({ openMessage }: useDeleteAccountProps) => {
  const { user } = useUser();
  const [deleteAccountModalVisible, setDeleteAccountModalVisible] =
    useState(false);
  const lastSendDeleteAccount = useRef<number | undefined>(undefined);
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);

  const onPressDeleteAccount = () => {
    const now: any = new Date();
    if (
      lastSendDeleteAccount.current &&
      Math.abs(now - lastSendDeleteAccount.current) <= 60000
    ) {
      const seconds = Math.round(
        60 - (now - lastSendDeleteAccount.current) / 1000
      );
      const message = t("delete_account_modal.email_sent_wait", { seconds });
      openMessage(message, "error");
    } else {
      setDeleteAccountModalVisible(true);
    }
  };

  const onDeleteAccountSuccess = () => {
    lastSendDeleteAccount.current = new Date().getTime();
  };

  return {
    deleteAccountModalVisible,
    closeDeleteAccountModal: () => setDeleteAccountModalVisible(false),
    onPressDeleteAccount,
    showDeleteAccountButton: !!user?.email,
    onDeleteAccountSuccess,
  };
};
